/**
 * Opens the "Report a problem" form and the "What's new" walkthrough from
 * anywhere: the top-bar button, the user menu, Help > Suggest a Feature.
 * The hosts (FeedbackHost, WhatsNewHost) are mounted once by the shell and
 * subscribe here. In memory only.
 */
import { useSyncExternalStore } from 'react';

/** Fields a caller fills in ahead (Help Center "Request a change", Ask Velora). */
export interface FeedbackPrefill {
  type?: 'bug' | 'improvement' | 'feature' | 'other';
  title?: string;
  description?: string;
  /** Module id for "Part of the app". */
  area?: string;
}

export interface FeedbackUiState {
  requestOpen: boolean;
  prefill: FeedbackPrefill | null;
  /** `new`: after sign-in, undismissed only. `all`: reopened from the user menu. */
  whatsNew: 'new' | 'all' | null;
}

let state: FeedbackUiState = { requestOpen: false, prefill: null, whatsNew: null };
const listeners = new Set<() => void>();

function set(next: Partial<FeedbackUiState>): void {
  state = { ...state, ...next };
  for (const l of listeners) l();
}

export const feedbackUi = {
  get: (): FeedbackUiState => state,
  subscribe(l: () => void): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  openRequest: (prefill: FeedbackPrefill | null = null) => set({ requestOpen: true, prefill }),
  setRequestOpen: (open: boolean) =>
    set(open ? { requestOpen: true } : { requestOpen: false, prefill: null }),
  openWhatsNew: (mode: 'new' | 'all') => set({ whatsNew: mode }),
  closeWhatsNew: () => set({ whatsNew: null }),
  /** Tests only. */
  reset: () => set({ requestOpen: false, prefill: null, whatsNew: null }),
};

export function useFeedbackUi(): FeedbackUiState {
  return useSyncExternalStore(feedbackUi.subscribe, feedbackUi.get, feedbackUi.get);
}

/** Opens the request form for the current page, optionally filled in. */
export function openFeedback(prefill?: FeedbackPrefill): void {
  feedbackUi.openRequest(prefill ?? null);
}
